import { datan2, dcos, dsin, PI, TWO_PI } from '../sim/dmath';
import { arcDist, bridgeHalf, CuttingQuery } from './crossing';
import { Rng } from './prng';
import type { TrackData } from './types';

/**
 * Where every piece of scenery goes. Pure data from the date seed, so every
 * player sees the same circuit dressing. Coordinates are the simulation's
 * (x, y); rot is a yaw in the same frame (0 = facing +x).
 */
export type SceneryKind =
  | 'cypress'
  | 'pine'
  | 'olive'
  | 'bush'
  | 'bale'
  | 'baleStack'
  | 'wall'
  | 'fence'
  | 'farmhouse'
  | 'spectator'
  | 'sponsor'
  | 'brakingBoard'
  | 'flag';

export interface Placement {
  kind: SceneryKind;
  x: number;
  y: number;
  rot: number;
  scale: number;
  variant: number;
  /** For wall/fence pieces: length along rot. */
  length?: number;
}

export interface Landmark {
  x: number;
  y: number;
  rot: number;
}

export interface SceneryPlan {
  items: Placement[];
  grandstand: Landmark & { length: number; depth: number };
  gantry: Landmark & { span: number };
  billboard: Landmark;
  town: Landmark & { radius: number; height: number };
  /** Direction the sun comes from (yaw in sim frame): ahead-right of the start straight. */
  sunYaw: number;
  bounds: { minX: number; maxX: number; minY: number; maxY: number; cx: number; cy: number };
}

/**
 * Spatial hash of the centerline samples for fast "how far from the track"
 * queries (render only: terrain and scenery). A query looks only where there
 * is track at all, and searches outward ring by ring, stopping as soon as no
 * farther ring could hold anything closer: most of the landscape is far from
 * the track, and building it asks tens of thousands of times.
 */
export class TrackProximity {
  private cells = new Map<number, number[]>();
  /** The cells that hold any track. */
  private minCX = Infinity;
  private maxCX = -Infinity;
  private minCY = Infinity;
  private maxCY = -Infinity;

  constructor(private t: TrackData, private cell = 24) {
    for (let i = 0; i < t.n; i++) {
      const cx = Math.floor(t.px[i] / cell);
      const cy = Math.floor(t.py[i] / cell);
      this.minCX = Math.min(this.minCX, cx);
      this.maxCX = Math.max(this.maxCX, cx);
      this.minCY = Math.min(this.minCY, cy);
      this.maxCY = Math.max(this.maxCY, cy);
      const k = this.key(cx, cy);
      const arr = this.cells.get(k);
      if (arr) arr.push(i);
      else this.cells.set(k, [i]);
    }
  }

  private key(cx: number, cy: number): number {
    return (cx + 5000) * 10007 + (cy + 5000);
  }

  /** Nearest sample in the cells within `range` metres: [index, distance], or [-1, Infinity]. */
  nearest(x: number, y: number, range = 120): [number, number] {
    const c = this.cell;
    const r = Math.ceil(range / c);
    const cx = Math.floor(x / c);
    const cy = Math.floor(y / c);
    // The search window, cut down to where there is track.
    const x0 = Math.max(cx - r, this.minCX);
    const x1 = Math.min(cx + r, this.maxCX);
    const y0 = Math.max(cy - r, this.minCY);
    const y1 = Math.min(cy + r, this.maxCY);
    if (x0 > x1 || y0 > y1) return [-1, Infinity];
    let best = -1;
    let bd = Infinity;
    const scan = (gx: number, gy: number): void => {
      if (gx < x0 || gx > x1 || gy < y0 || gy > y1) return;
      const arr = this.cells.get(this.key(gx, gy));
      if (!arr) return;
      for (const i of arr) {
        const ex = this.t.px[i] - x;
        const ey = this.t.py[i] - y;
        const d = Math.sqrt(ex * ex + ey * ey);
        if (d < bd) {
          bd = d;
          best = i;
        }
      }
    };
    for (let k = 0; k <= r; k++) {
      // Everything in ring k is at least (k - 1) cells away: nothing there can beat what's found.
      if (k > 0 && (k - 1) * c > bd) break;
      for (let dy = -k; dy <= k; dy++) {
        if (dy === -k || dy === k) {
          for (let dx = -k; dx <= k; dx++) scan(cx + dx, cy + dy);
        } else {
          scan(cx - k, cy + dy);
          if (k > 0) scan(cx + k, cy + dy);
        }
      }
    }
    return [best, bd];
  }

  /** Distance from the barrier line outward (negative = inside the barrier). */
  clearance(x: number, y: number, range = 120): number {
    const [i, d] = this.nearest(x, y, range);
    if (i < 0) return range;
    const t = this.t;
    const side = (x - t.px[i]) * -t.ty[i] + (y - t.py[i]) * t.tx[i] >= 0 ? 1 : -1;
    const barrier = side > 0 ? t.barrierLeft[i] : t.barrierRight[i];
    return d - barrier;
  }
}

/** Keeps placed objects from overlapping. */
class Occupancy {
  private cells = new Map<number, { x: number; y: number; r: number }[]>();
  constructor(private cell = 16) {}
  private key(cx: number, cy: number): number {
    return (cx + 5000) * 10007 + (cy + 5000);
  }
  free(x: number, y: number, r: number): boolean {
    const c = this.cell;
    const cx = Math.floor(x / c);
    const cy = Math.floor(y / c);
    const span = Math.ceil((r + 16) / c);
    for (let dy = -span; dy <= span; dy++) {
      for (let dx = -span; dx <= span; dx++) {
        const arr = this.cells.get(this.key(cx + dx, cy + dy));
        if (!arr) continue;
        for (const o of arr) {
          const ex = o.x - x;
          const ey = o.y - y;
          if (ex * ex + ey * ey < (o.r + r) * (o.r + r)) return false;
        }
      }
    }
    return true;
  }
  add(x: number, y: number, r: number): void {
    const k = this.key(Math.floor(x / this.cell), Math.floor(y / this.cell));
    const arr = this.cells.get(k);
    if (arr) arr.push({ x, y, r });
    else this.cells.set(k, [{ x, y, r }]);
  }
}

const RADIUS: Record<SceneryKind, number> = {
  cypress: 1.6,
  pine: 5.5,
  olive: 3,
  bush: 1.3,
  bale: 0.8,
  baleStack: 1.8,
  wall: 0,
  fence: 0,
  farmhouse: 13,
  spectator: 0.4,
  sponsor: 0,
  brakingBoard: 0,
  flag: 0.3,
};

export function planScenery(t: TrackData, seed: string): SceneryPlan {
  const rng = Rng.fromString('scenery|' + seed);
  const prox = new TrackProximity(t);
  const occ = new Occupancy();
  const items: Placement[] = [];
  const n = t.n;
  const nx = (i: number): number => -t.ty[i];
  const ny = (i: number): number => t.tx[i];
  const tangentYaw = (i: number): number => datan2(t.ty[i], t.tx[i]);
  const barrier = (i: number, side: number): number => (side > 0 ? t.barrierLeft[i] : t.barrierRight[i]);
  const at = (i: number, side: number, off: number): [number, number] => [t.px[i] + nx(i) * side * off, t.py[i] + ny(i) * side * off];

  // Bounds.
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    minX = Math.min(minX, t.px[i]);
    maxX = Math.max(maxX, t.px[i]);
    minY = Math.min(minY, t.py[i]);
    maxY = Math.max(maxY, t.py[i]);
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  const place = (p: Placement, r = RADIUS[p.kind]): boolean => {
    if (r > 0) {
      if (!occ.free(p.x, p.y, r)) return false;
      occ.add(p.x, p.y, r);
    }
    items.push(p);
    return true;
  };
  // A crossover: nothing down in the cutting or on the bridge but the structures themselves.
  const cx0 = t.crossing ?? null;
  const cut = cx0 ? new CuttingQuery(t, cx0) : null;
  const bridge = cx0 ? bridgeHalf(cx0) + 6 : 0;
  const offLimits = (x: number, y: number, i = -1): boolean =>
    !!cx0 && ((i >= 0 && arcDist(t.s[i], cx0.sOver, t.length) <= bridge) || (!!cut && cut.inside(x, y, 7)));
  const clearOf = (x: number, y: number, margin: number): boolean => prox.clearance(x, y) > margin && !offLimits(x, y);
  // v3 set pieces: a street section between walls, a wall along the top of a banked curve.
  const inSection = (name: string, i: number): boolean => {
    const s = t.s[i];
    return (t.sections ?? []).some((q) => q.name === name && ((q.from <= q.to && s >= q.from && s <= q.to) || (q.from > q.to && (s >= q.from || s <= q.to))));
  };
  const walledSide = (i: number, side: number): boolean => inSection('streets', i) || (!!t.bank && Math.abs(t.bank[i]) > 0.08 && Math.sign(t.bank[i]) === side);

  // ---- start/finish: gantry at sample 0, grandstand along the straight
  const startYaw = tangentYaw(0);
  const gantry = { x: t.px[0], y: t.py[0], rot: startYaw, span: (t.halfWidth + t.curbWidth + 1.6) * 2 };
  let gsSide = 1;
  let gl = 0;
  let gr = 0;
  for (let i = 0; i < 30; i++) {
    gl += t.barrierLeft[i];
    gr += t.barrierRight[i];
  }
  gsSide = gl >= gr ? 1 : -1;
  const gsIndex = 12;
  const gsDepth = 9;
  const [gsx, gsy] = at(gsIndex, gsSide, barrier(gsIndex, gsSide) + 1.8 + gsDepth / 2);
  // The grandstand faces the track: its front (+x local) points back across the circuit.
  const grandstand = { x: gsx, y: gsy, rot: tangentYaw(gsIndex) - (gsSide * PI) / 2, length: 46, depth: gsDepth };
  occ.add(gsx, gsy, 24);

  // ---- biggest braking zone: the poster billboard, facing the approach
  const hb = t.features.heavyBraking;
  const bbSide = -hb.dir;
  let billboard = { x: 0, y: 0, rot: 0 };
  for (const back of [70, 55, 85, 40, 100, 30]) {
    const bbIndex = (hb.cornerIndex - Math.round(back / (t.length / n)) + n) % n;
    const [bbx, bby] = at(bbIndex, bbSide, barrier(bbIndex, bbSide) + 6);
    billboard = { x: bbx, y: bby, rot: tangentYaw(bbIndex) + PI + bbSide * 0.55 };
    if (occ.free(bbx, bby, 7)) break;
  }
  occ.add(billboard.x, billboard.y, 7);

  // ---- barrier dressing, both sides, in stretches
  const ds = t.length / n;
  // Gravel traps and painted tarmac runoff both get rows of bales along their barrier.
  const gravelSide = (i: number, side: number): boolean => {
    const r = side > 0 ? t.runoffLeft[i] : t.runoffRight[i];
    return r === 3 || r === 5;
  };
  for (const side of [1, -1]) {
    let i = 0;
    while (i < n) {
      const nearStart = i < 40 || i > n - 25;
      let kind: 'bales' | 'wall' | 'fence' | 'open';
      if (gravelSide(i, side)) kind = 'bales';
      else if (nearStart && side === gsSide) kind = 'wall';
      else {
        const r = rng.next();
        kind = r < 0.3 ? 'wall' : r < 0.5 ? 'fence' : r < 0.78 ? 'bales' : 'open';
      }
      const len = kind === 'bales' && gravelSide(i, side) ? 1 : rng.int(12, 34);
      for (let k = 0; k < len && i < n; k++, i++) {
        const b = barrier(i, side) + 0.9;
        const [x, y] = at(i, side, b);
        if (prox.clearance(x, y) < 0.5) continue;
        if (offLimits(x, y, i)) continue;
        const yaw = tangentYaw(i);
        if (walledSide(i, side)) {
          if (i % 2 === 0) place({ kind: 'wall', x, y, rot: yaw, scale: 1, variant: rng.int(0, 7), length: ds * 2 + 0.2 }, 0);
          continue;
        }
        if (kind === 'bales') {
          // A row of bales lying along the track, nearly touching.
          if (i % 1 === 0) place({ kind: 'bale', x, y, rot: yaw, scale: 1, variant: rng.int(0, 3) }, 0);
        } else if (kind === 'wall' && i % 2 === 0) {
          place({ kind: 'wall', x, y, rot: yaw, scale: 1, variant: rng.int(0, 7), length: ds * 2 + 0.2 }, 0);
        } else if (kind === 'fence' && i % 2 === 0) {
          place({ kind: 'fence', x, y, rot: yaw, scale: 1, variant: 0, length: ds * 2 + 0.1 }, 0);
        }
      }
    }
  }

  // ---- braking boards 150 / 100 / 50 before the big braking zones
  for (const z of t.brakingZones) {
    if (!z.major) continue;
    const side = z.dir;
    for (const [dist, v] of [[150, 0], [100, 1], [50, 2]] as const) {
      const i = (z.cornerIndex - Math.round(dist / ds) + n) % n;
      if (cx0 && arcDist(t.s[i], cx0.sOver, t.length) <= bridge) continue;
      const off = t.halfWidth + (side > 0 ? t.curbLeft[i] : t.curbRight[i]) * t.curbWidth + 2.6;
      const [x, y] = at(i, side, off);
      place({ kind: 'brakingBoard', x, y, rot: tangentYaw(i) + PI, scale: 1, variant: v }, 0);
    }
  }

  // ---- sponsor boards along the straights, just behind the barrier
  for (const s of t.straights) {
    if (s.length < 60) continue;
    const side = rng.chance(0.5) ? 1 : -1;
    const count = Math.min(6, Math.floor(s.length / 40));
    let i = (s.start + 6) % n;
    for (let k = 0; k < count; k++) {
      const [x, y] = at(i, side, barrier(i, side) + 2.2);
      if (occ.free(x, y, 2) && prox.clearance(x, y) > 1 && !offLimits(x, y, i)) {
        place({ kind: 'sponsor', x, y, rot: tangentYaw(i) - (side * PI) / 2, scale: 1, variant: rng.int(0, 7) }, 0);
        occ.add(x, y, 3);
      }
      i = (i + rng.int(7, 11)) % n;
    }
  }

  // ---- spectators: clusters behind the barrier at the best corners
  const crowdSpots: [number, number][] = [];
  const f = t.features;
  if (f.hairpin) crowdSpots.push([f.hairpin.apex, -f.hairpin.dir]);
  if (f.chicane) crowdSpots.push([f.chicane[0].apex, -f.chicane[0].dir]);
  if (f.sweeper) crowdSpots.push([f.sweeper.start, -f.sweeper.dir]);
  if (f.esses) for (const c of f.esses.slice(0, 2)) crowdSpots.push([c.apex, -c.dir]);
  // The day's showpiece draws a crowd (both sides of the stadium and the streets).
  for (const q of t.sections ?? []) {
    if (q.name !== t.signature && q.name !== 'mountainDown') continue;
    const span = (((q.to - q.from) % t.length) + t.length) % t.length;
    const spots = Math.max(1, Math.min(4, Math.round(span / 70)));
    for (let k = 0; k < spots; k++) {
      const s = (q.from + (span * (k + 0.5)) / spots) % t.length;
      const i = Math.min(n - 1, Math.round((s / t.length) * n));
      const both = q.name === 'stadium' || q.name === 'streets';
      const c = t.curvature[i];
      crowdSpots.push([i, c > 0 ? -1 : c < 0 ? 1 : k % 2 ? 1 : -1]);
      if (both) crowdSpots.push([i, c > 0 ? 1 : -1]);
    }
  }
  crowdSpots.push([(n - 18) % n, gsSide]);
  crowdSpots.push([30, -gsSide]);
  for (const [apex, side] of crowdSpots) {
    const count = rng.int(9, 16);
    for (let k = 0; k < count; k++) {
      const i = (apex + Math.round((k - count / 2) * 0.75) + n) % n;
      const row = k % 3 === 2 ? 1 : 0;
      const [x, y] = at(i, side, barrier(i, side) + 2.6 + row * 1.3 + rng.range(-0.2, 0.2));
      if (prox.clearance(x, y) < 1.2) continue;
      if (offLimits(x, y, i)) continue;
      place({ kind: 'spectator', x, y, rot: tangentYaw(i) - (side * PI) / 2 + rng.range(-0.25, 0.25), scale: rng.range(0.92, 1.08), variant: rng.int(0, 11) });
    }
  }

  // ---- a farmhouse in view of the start, across from the grandstand
  let houses = 0;
  for (let tries = 0; tries < 30; tries++) {
    const i = (rng.int(20, 45) + n) % n;
    const [x, y] = at(i, -gsSide, barrier(i, -gsSide) + rng.range(26, 48));
    if (!clearOf(x, y, 18)) continue;
    if (place({ kind: 'farmhouse', x, y, rot: tangentYaw(i) + (gsSide * PI) / 2 + rng.range(-0.3, 0.3), scale: 1.05, variant: rng.int(0, 2) })) {
      houses++;
      break;
    }
  }

  // ---- farmhouses in open ground, each with its own trees
  for (let tries = 0; tries < 400 && houses < rng.int(3, 5); tries++) {
    const a = rng.range(0, TWO_PI);
    const r = rng.range(60, 360);
    const x = cx + dcos(a) * r * 1.2;
    const y = cy + dsin(a) * r;
    if (!clearOf(x, y, 22)) continue;
    const rot = rng.range(0, TWO_PI);
    if (!place({ kind: 'farmhouse', x, y, rot, scale: rng.range(0.9, 1.1), variant: rng.int(0, 2) })) continue;
    houses++;
    for (let k = 0; k < rng.int(3, 6); k++) {
      const aa = rot + rng.range(-1.2, 1.2) + (k % 2 ? PI : 0);
      const rr = rng.range(15, 24);
      const tx = x + dcos(aa) * rr;
      const ty = y + dsin(aa) * rr;
      if (clearOf(tx, ty, 3)) place({ kind: 'cypress', x: tx, y: ty, rot: rng.range(0, TWO_PI), scale: rng.range(0.8, 1.15), variant: rng.int(0, 3) });
    }
    for (let k = 0; k < 6; k++) {
      const aa = rng.range(0, TWO_PI);
      const rr = rng.range(12, 20);
      const bx = x + dcos(aa) * rr;
      const by = y + dsin(aa) * rr;
      if (clearOf(bx, by, 2)) place({ kind: 'bush', x: bx, y: by, rot: aa, scale: rng.range(0.8, 1.3), variant: rng.int(0, 3) });
    }
  }

  // ---- cypress rows (lanes) and clusters
  const lanes = rng.int(9, 14);
  for (let l = 0; l < lanes; l++) {
    const a = rng.range(0, TWO_PI);
    const r = rng.range(40, 520);
    let x = cx + dcos(a) * r * 1.2;
    let y = cy + dsin(a) * r;
    const dir = rng.range(0, TWO_PI);
    const step = rng.range(6.5, 8.5);
    const count = rng.int(4, 11);
    for (let k = 0; k < count; k++) {
      if (clearOf(x, y, 4)) place({ kind: 'cypress', x, y, rot: rng.range(0, TWO_PI), scale: rng.range(0.85, 1.2), variant: rng.int(0, 3) });
      x += dcos(dir) * step;
      y += dsin(dir) * step;
    }
  }
  for (let c = 0; c < 95; c++) {
    // Clusters hug the circuit: a random sample, pushed out beyond the barrier.
    const i = rng.int(0, n - 1);
    const side = rng.chance(0.5) ? 1 : -1;
    const [bx, by] = at(i, side, barrier(i, side) + rng.range(4, 45));
    const count = rng.int(2, 5);
    for (let k = 0; k < count; k++) {
      const x = bx + rng.range(-7, 7);
      const y = by + rng.range(-7, 7);
      if (clearOf(x, y, 4)) place({ kind: 'cypress', x, y, rot: rng.range(0, TWO_PI), scale: rng.range(0.75, 1.2), variant: rng.int(0, 3) });
    }
  }

  // ---- tall umbrella pines framing the start/finish area (the postcard shot)
  for (let k = 0; k < 14; k++) {
    const i = (n - 30 + k * 6 + rng.int(-2, 2) + n) % n;
    const side = k % 3 === 0 ? -gsSide : gsSide;
    const [x, y] = at(i, side, barrier(i, side) + (side === gsSide ? rng.range(13, 26) : rng.range(4, 16)));
    if (clearOf(x, y, 3)) place({ kind: 'pine', x, y, rot: rng.range(0, TWO_PI), scale: rng.range(1.15, 1.4), variant: rng.int(0, 3) });
  }

  // ---- umbrella pines: loners and pairs
  for (let p = 0; p < 95; p++) {
    const i = rng.int(0, n - 1);
    const side = rng.chance(0.5) ? 1 : -1;
    const [x, y] = at(i, side, barrier(i, side) + rng.range(7, 120));
    if (!clearOf(x, y, 5)) continue;
    place({ kind: 'pine', x, y, rot: rng.range(0, TWO_PI), scale: rng.range(0.8, 1.2), variant: rng.int(0, 3) });
  }

  // ---- olive groves
  for (let gIdx = 0; gIdx < rng.int(4, 7); gIdx++) {
    const a = rng.range(0, TWO_PI);
    const r = rng.range(60, 420);
    const ox = cx + dcos(a) * r * 1.2;
    const oy = cy + dsin(a) * r;
    const yaw = rng.range(0, TWO_PI);
    const cols = rng.int(3, 5);
    const rows = rng.int(3, 5);
    for (let u = 0; u < cols; u++) {
      for (let v = 0; v < rows; v++) {
        const lx = (u - cols / 2) * 9;
        const ly = (v - rows / 2) * 9;
        const x = ox + dcos(yaw) * lx - dsin(yaw) * ly + rng.range(-1, 1);
        const y = oy + dsin(yaw) * lx + dcos(yaw) * ly + rng.range(-1, 1);
        if (clearOf(x, y, 4)) place({ kind: 'olive', x, y, rot: rng.range(0, TWO_PI), scale: rng.range(0.85, 1.15), variant: rng.int(0, 3) });
      }
    }
  }

  // ---- bushes near the barrier and hay stacks in the fields
  for (let b = 0; b < 260; b++) {
    const i = rng.int(0, n - 1);
    const side = rng.chance(0.5) ? 1 : -1;
    const [x, y] = at(i, side, barrier(i, side) + rng.range(2.5, 40));
    if (clearOf(x, y, 1.5)) place({ kind: 'bush', x, y, rot: rng.range(0, TWO_PI), scale: rng.range(0.7, 1.4), variant: rng.int(0, 3) });
  }
  for (let h = 0; h < 18; h++) {
    const i = rng.int(0, n - 1);
    const side = rng.chance(0.5) ? 1 : -1;
    const [x, y] = at(i, side, barrier(i, side) + rng.range(6, 90));
    if (clearOf(x, y, 3)) place({ kind: rng.chance(0.5) ? 'baleStack' : 'bale', x, y, rot: rng.range(0, TWO_PI), scale: 1, variant: rng.int(0, 3) });
  }

  // ---- flags around the grandstand
  for (let k = 0; k < 6; k++) {
    const i = (gsIndex - 14 + k * 6 + n) % n;
    const [x, y] = at(i, gsSide, barrier(i, gsSide) + 1.6);
    place({ kind: 'flag', x, y, rot: tangentYaw(i), scale: 1, variant: k % 2 }, 0);
  }

  // ---- the hilltop town, in the distance roughly toward the sun
  const sunYaw = startYaw - 0.5;
  // The town sits just right of the start straight's heading: the postcard view.
  const townYaw = startYaw - rng.range(0.12, 0.3);
  const townDist = Math.max(maxX - minX, maxY - minY) * 0.5 + rng.range(500, 560);
  const town = { x: cx + dcos(townYaw) * townDist, y: cy + dsin(townYaw) * townDist, rot: rng.range(0, TWO_PI), radius: 110, height: rng.range(66, 76) };

  return { items, grandstand, gantry, billboard, town, sunYaw, bounds: { minX, maxX, minY, maxY, cx, cy } };
}
